// Combat minigame (Phase 5 evolution of the v1 block/counter port).
// - Normal encounters: BLOCK → COUNTER; window scales with the enemy's
//   aggressiveness (v1 was a fixed 2.5s).
// - Sensei encounters: 3 rounds of mixed BLOCK/COUNTER/DODGE cues with
//   shrinking windows (2.5 → 1.8 → 1.2). DODGE = jump/tap.
// - "Perfect" timing band: reacting within the first 30% of a window pays a
//   bonus per perfect.
// Core numbers from v1 COMBAT_CONFIG (js/constants.js:595-620).

import type * as THREE from 'three';
import type { Enemy } from '@/entities/enemies/enemy';

export const COMBAT = {
  TRIGGER_DISTANCE: 1.8,
  TIME_SCALE: 0.1,
  TRANSITION_IN: 0.8,
  TRANSITION_OUT: 0.5,
  BASE_WINDOW: 2.5, // v1 BLOCK_WINDOW / COUNTER_WINDOW
  PERFECT_BAND: 0.3, // First 30% of the window
  SENSEI_WINDOWS: [2.5, 1.8, 1.2],
} as const;

export type CombatAction = 'block' | 'counter' | 'dodge';
export type CombatPhase = 'inactive' | 'intro' | 'cue' | 'result';

export interface CueStep {
  action: CombatAction;
  window: number;
}

export interface CombatResult {
  victory: boolean;
  perfects: number;
  sensei: boolean;
}

export interface CombatStrings {
  block: string;
  counter: string;
  dodge: string;
  blockPrompt: string;
  counterPrompt: string;
  dodgePrompt: string;
  win: string;
  lose: string;
  senseiIntro: string;
}

/** Normal-combat window scaled by aggressiveness (punk 0.9 → tight ~1.9s,
 *  hipster 0.4 → roomy ~2.4s). Exported for unit tests. */
export function windowForAggressiveness(aggressiveness: number): number {
  return COMBAT.BASE_WINDOW * (1.15 - aggressiveness * 0.45);
}

export function normalSteps(aggressiveness: number): CueStep[] {
  const window = windowForAggressiveness(aggressiveness);
  return [
    { action: 'block', window },
    { action: 'counter', window },
  ];
}

export function senseiSteps(roll: () => number): CueStep[] {
  const actions: CombatAction[] = ['block', 'counter', 'dodge'];
  return COMBAT.SENSEI_WINDOWS.map((window) => ({
    action: actions[Math.floor(roll() * actions.length)] as CombatAction,
    window,
  }));
}

export class CombatSystem {
  phase: CombatPhase = 'inactive';
  timeScale = 1.0;
  enemy: Enemy | null = null;
  onComplete: ((result: CombatResult, enemy: Enemy | null) => void) | null = null;

  private steps: CueStep[] = [];
  private stepIndex = 0;
  private timer = 0;
  private victory = false;
  private perfects = 0;
  private sensei = false;
  private retriesLeft = 0;
  private blockOnLeft = true;
  private readonly overlay: HTMLElement;
  private readonly prompt: HTMLElement;
  private readonly leftButton: HTMLButtonElement;
  private readonly rightButton: HTMLButtonElement;

  constructor(
    root: HTMLElement,
    private readonly strings: CombatStrings,
  ) {
    this.overlay = document.createElement('div');
    this.overlay.className = 'combat-overlay';
    this.overlay.hidden = true;
    this.overlay.innerHTML = `
      <div class="combat-prompt"></div>
      <button class="combat-btn combat-left"></button>
      <button class="combat-btn combat-right"></button>
    `;
    root.appendChild(this.overlay);
    this.prompt = this.overlay.querySelector('.combat-prompt') as HTMLElement;
    this.leftButton = this.overlay.querySelector('.combat-left') as HTMLButtonElement;
    this.rightButton = this.overlay.querySelector('.combat-right') as HTMLButtonElement;
    this.leftButton.addEventListener('click', () => this.pressSide('left'));
    this.rightButton.addEventListener('click', () => this.pressSide('right'));
  }

  get isActive(): boolean {
    return this.phase !== 'inactive';
  }

  shouldTrigger(playerPos: THREE.Vector3, enemyPos: THREE.Vector3): boolean {
    return playerPos.distanceTo(enemyPos) < COMBAT.TRIGGER_DISTANCE;
  }

  start(enemy: Enemy, steps: CueStep[], sensei: boolean, retries = 0): void {
    if (this.isActive) return;
    this.enemy = enemy;
    enemy.inCombat = true;
    this.steps = steps;
    this.stepIndex = 0;
    this.phase = 'intro';
    this.timer = 0;
    this.victory = false;
    this.perfects = 0;
    this.sensei = sensei;
    this.retriesLeft = retries; // Second Chance modifier (Phase 6)
    this.blockOnLeft = Math.random() < 0.5; // Randomized per encounter (v1)
    this.leftButton.textContent = `${this.blockOnLeft ? this.strings.block : this.strings.counter} (←)`;
    this.rightButton.textContent = `${this.blockOnLeft ? this.strings.counter : this.strings.block} (→)`;
    this.overlay.hidden = false;
    this.setPrompt(sensei ? this.strings.senseiIntro : '', '#FFD700');
    this.setButtonsEnabled(false);
  }

  /** Keyboard/buttons: ←/→ map by position (v1 behavior). */
  pressSide(side: 'left' | 'right'): void {
    const action: CombatAction = (side === 'left') === this.blockOnLeft ? 'block' : 'counter';
    this.press(action);
  }

  press(action: CombatAction): void {
    if (this.phase !== 'cue') return;
    const step = this.steps[this.stepIndex];
    if (!step) return;

    if (action !== step.action) {
      this.failCue();
      return;
    }
    if (this.timer <= step.window * COMBAT.PERFECT_BAND) this.perfects++;
    this.stepIndex++;
    if (this.stepIndex >= this.steps.length) {
      this.finishWith(true);
    } else {
      this.timer = 0;
      this.showCue();
    }
  }

  private showCue(): void {
    const step = this.steps[this.stepIndex];
    if (!step) return;
    const prompts: Record<CombatAction, [string, string]> = {
      block: [this.strings.blockPrompt, '#00E5FF'],
      counter: [this.strings.counterPrompt, '#FF6600'],
      dodge: [this.strings.dodgePrompt, '#B98CFF'],
    };
    const [text, color] = prompts[step.action];
    this.setPrompt(
      this.sensei ? `${this.stepIndex + 1}/${this.steps.length} · ${text}` : text,
      color,
    );
    this.highlight(step.action);
  }

  /** A wrong press or timeout: Second Chance forgives one per fight. */
  private failCue(): void {
    if (this.retriesLeft > 0) {
      this.retriesLeft--;
      this.timer = 0;
      this.showCue();
      return;
    }
    this.finishWith(false);
  }

  private finishWith(victory: boolean): void {
    this.victory = victory;
    this.phase = 'result';
    this.timer = 0;
    this.setButtonsEnabled(false);
    this.setPrompt(victory ? this.strings.win : this.strings.lose, victory ? '#7FFF00' : '#FF4444');
  }

  /** Real (unscaled) dt — combat UI timing never dilates (v1 game3d.js:514). */
  update(dt: number): void {
    if (!this.isActive) return;
    this.timer += dt;

    switch (this.phase) {
      case 'intro':
        this.timeScale = Math.max(
          COMBAT.TIME_SCALE,
          1 - this.easedProgress(COMBAT.TRANSITION_IN) * 0.9,
        );
        if (this.timer >= COMBAT.TRANSITION_IN) {
          this.phase = 'cue';
          this.timer = 0;
          this.setButtonsEnabled(true);
          this.showCue();
        }
        break;
      case 'cue': {
        const step = this.steps[this.stepIndex];
        if (step && this.timer >= step.window) this.failCue(); // Timeout loses (v1)
        break;
      }
      case 'result':
        this.timeScale = COMBAT.TIME_SCALE + this.easedProgress(COMBAT.TRANSITION_OUT) * 0.9;
        if (this.timer >= COMBAT.TRANSITION_OUT) this.end();
        break;
      case 'inactive':
        break;
    }
  }

  private end(): void {
    const enemy = this.enemy;
    const result: CombatResult = {
      victory: this.victory,
      perfects: this.perfects,
      sensei: this.sensei,
    };
    this.phase = 'inactive';
    this.timeScale = 1.0;
    this.enemy = null;
    this.overlay.hidden = true;
    if (enemy) enemy.inCombat = false;
    this.onComplete?.(result, enemy);
  }

  /** Abort without result (game over from elsewhere, run reset). */
  cancel(): void {
    if (!this.isActive) return;
    if (this.enemy) this.enemy.inCombat = false;
    this.enemy = null;
    this.phase = 'inactive';
    this.timeScale = 1.0;
    this.overlay.hidden = true;
  }

  private easedProgress(duration: number): number {
    const progress = Math.min(this.timer / duration, 1);
    return 1 - Math.pow(1 - progress, 3); // v1 cubic ease
  }

  private setPrompt(text: string, color: string): void {
    this.prompt.textContent = text;
    this.prompt.style.color = color;
  }

  private setButtonsEnabled(enabled: boolean): void {
    this.leftButton.disabled = !enabled;
    this.rightButton.disabled = !enabled;
  }

  private highlight(action: CombatAction): void {
    const blockButton = this.blockOnLeft ? this.leftButton : this.rightButton;
    const counterButton = this.blockOnLeft ? this.rightButton : this.leftButton;
    blockButton.classList.toggle('active', action === 'block');
    counterButton.classList.toggle('active', action === 'counter');
    if (action === 'dodge') {
      blockButton.classList.remove('active');
      counterButton.classList.remove('active');
    }
  }
}
